import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, X, Check, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Inline camera capture that uploads to /api/files/upload (category=patient_photo).
 *
 * Camera fix: we ALWAYS mount the <video> element (hidden via opacity/CSS until ready).
 * Earlier the video element was conditionally rendered, which created a race where
 * `videoRef.current` was null at the moment `getUserMedia` returned, so attaching the
 * stream silently failed. Now the ref is stable and the stream attaches via a useEffect
 * tied to the live `stream` state.
 *
 * Box size is intentionally compact (160×160 px) to match the rest of the registration form.
 *
 * Props:
 *  - onUploaded(fileRecord): callback after upload succeeds.
 *  - category: defaults to 'patient_photo'.
 *  - size: 'sm' (default — 160px square) | 'md' (240px square).
 */
export default function WebcamCapture({ onUploaded, category = "patient_photo", size = "sm" }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);     // live MediaStream (drives the useEffect attachment)
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [blob, setBlob] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Capability detection
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices && !!navigator.mediaDevices.getUserMedia;

  // Attach stream to the (always-mounted) video element whenever the stream changes.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (stream) {
      v.srcObject = stream;
      // Some browsers (Safari iOS) require an explicit play() after srcObject is set.
      const playPromise = v.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => { /* autoplay blocked — user can still click capture */ });
      }
    } else {
      v.srcObject = null;
    }
  }, [stream]);

  // Cleanup on unmount
  useEffect(() => () => { stream?.getTracks().forEach((t) => t.stop()); }, [stream]);

  const start = async () => {
    setError("");
    if (!supported) {
      const msg = window.isSecureContext === false
        ? "Camera access requires HTTPS. Open this page via the secure URL."
        : "Your browser does not support camera capture. Try Chrome or Edge.";
      setError(msg);
      toast.error(msg);
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      });
      setStream(s);
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Camera permission denied. Click the camera icon in the address bar to allow access."
        : e?.name === "NotFoundError"
          ? "No camera detected on this device."
          : (e?.message || "Unable to start camera.");
      setError(msg);
      toast.error(msg);
    }
  };

  const stop = () => {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((b) => {
      if (!b) return;
      setBlob(b);
      setPreviewUrl(URL.createObjectURL(b));
      stop();
    }, "image/jpeg", 0.9);
  };

  const retake = () => {
    setBlob(null);
    setPreviewUrl("");
    start();
  };

  const upload = async () => {
    if (!blob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `webcam-${Date.now()}.jpg`, { type: "image/jpeg" }));
      const { data } = await api.post(`/files/upload?category=${category}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo captured");
      onUploaded?.(data);
      setBlob(null);
      setPreviewUrl("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const dim = size === "md" ? "h-60 w-60" : "h-40 w-40";

  return (
    <div className="space-y-2" data-testid="webcam-capture">
      <div className={`relative overflow-hidden rounded-md border border-border bg-secondary ${dim}`}>
        {/* Always-mounted video element. Hidden until a stream is attached. */}
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          className={`h-full w-full object-cover ${stream && !previewUrl ? "block" : "hidden"}`}
          data-testid="webcam-video"
        />
        {previewUrl && (
          <img src={previewUrl} alt="captured" className="h-full w-full object-cover" data-testid="webcam-preview" />
        )}
        {!stream && !previewUrl && (
          <div className="flex h-full w-full items-center justify-center text-center text-[11px] text-ink-muted">
            {error
              ? <span className="px-2 text-rose-600">{error}</span>
              : <span><Camera size={26} className="mx-auto text-ink-subtle" /><div className="mt-1.5">Click <b>Start</b> to capture</div></span>}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {!stream && !previewUrl && (
          <Button type="button" size="sm" onClick={start} className="h-8 bg-brand hover:bg-brand-700" data-testid="webcam-start">
            <Camera size={13} className="mr-1.5" />Start camera
          </Button>
        )}
        {stream && !previewUrl && (
          <>
            <Button type="button" size="sm" onClick={capture} className="h-8 bg-brand hover:bg-brand-700" data-testid="webcam-capture-btn">
              <Camera size={13} className="mr-1.5" />Capture
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={stop} className="h-8" data-testid="webcam-stop"><X size={13} className="mr-1" />Cancel</Button>
          </>
        )}
        {previewUrl && (
          <>
            <Button type="button" size="sm" onClick={upload} disabled={uploading} className="h-8 bg-leaf hover:bg-leaf/90 text-white" data-testid="webcam-upload">
              <Check size={13} className="mr-1.5" />{uploading ? "Uploading…" : "Use photo"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={retake} className="h-8" data-testid="webcam-retake">
              <RotateCcw size={13} className="mr-1" />Retake
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
